import type { DragEndEvent } from '@dnd-kit/core'
import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import type { Transform } from '@dnd-kit/utilities'
import { useEffect, useState } from 'react'

import { SortableItem } from './SortableItem'
import type { SortableItemProps } from './SortableItem'

type SortableHorizontalListProps = {
  wrapper: (children: React.ReactElement) => React.ReactElement
  layout?: 'horizontal' | 'vertical'
  items: SortableItemProps[]
  onSortEnd: (ids: string[]) => void
  transformStyle?: Partial<Transform>
}

export function SortableList(props: SortableHorizontalListProps) {
  const [ids, setIds] = useState(props.items.map((i) => i.id))
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 1 } }))

  function onDragEnd(e: DragEndEvent) {
    const { active, over } = e
    const activeId = active.id
    const overId = over?.id

    if (typeof activeId === 'string' && typeof overId === 'string' && activeId !== overId) {
      setIds((prev) => {
        const oldIndex = prev.indexOf(activeId)
        const newIndex = prev.indexOf(overId)
        const next = arrayMove(prev, oldIndex, newIndex)
        props.onSortEnd(next)
        return next
      })
    }
  }

  useEffect(() => {
    setIds(() => props.items.map((i) => i.id))
  }, [props.items])

  return (
    <DndContext sensors={sensors} onDragEnd={onDragEnd}>
      <SortableContext
        items={ids}
        strategy={props.layout === 'horizontal' ? horizontalListSortingStrategy : verticalListSortingStrategy}
      >
        {props.wrapper(
          <>
            {ids
              .map((id) => {
                const item = props.items.find((i) => i.id === id)
                return item !== undefined ? (
                  <SortableItem key={id} id={id} render={item.render} transformStyle={props.transformStyle} />
                ) : undefined
              })
              .compact()}
          </>,
        )}
      </SortableContext>
    </DndContext>
  )
}
